import * as THREE from 'three';

// Ping-pong pair of render targets for video-feedback visualizers - THE
// exception to the party-mode "no RenderTarget" contract, kept deliberately
// small and in one place because Echo is only the first user (Infinity and
// Focus are expected to follow).
//
// The shape of the loop every user runs per frame:
//
//   1. read  <- the PREVIOUS frame's image (this.read.texture)
//   2. draw INTO this.write: the transformed read texture + injected content
//   3. display this.write (or swap first and display read) on a quad in the
//      main scene
//   4. swap()
//
// Sizing follows the renderer's real drawing buffer times `scale`
// (feedback is inherently soft - half resolution is invisible, quarter is
// the tier-1 economy), and a size change REBUILDS both targets from black:
// stretching an old buffer into a new size produces a blurred, warped ghost
// of the previous framing, which in a feedback loop then re-amplifies
// itself forever. ensureSize() is therefore the resize policy, not just an
// allocator.
//
// RGBA8 + LinearFilter + no mipmaps + clamp: the cheapest sampleable target
// that a tiled GPU resolves without extra passes. No depth, no stencil -
// feedback scenes are ordered quads.
export class FeedbackBuffer {
  // options:
  //   scale        fraction of the drawing buffer per axis (0.5 = half res)
  //   width/height FIXED size in texels - a GPU-simulation grid (Silk's cloth
  //                nodes) rather than an image; overrides scale entirely and
  //                makes ensureSize independent of the drawing buffer
  //   type         THREE texture type (UnsignedByteType default; FloatType
  //                for simulation state - caller is responsible for having
  //                checked float-renderability, see silk.js's probe)
  //   filter       THREE filter (LinearFilter default; NearestFilter for
  //                simulation textures, where a texel is a NODE and
  //                interpolating between nodes is meaningless)
  //   wrap         THREE wrapping mode (ClampToEdgeWrapping default;
  //                RepeatWrapping for a periodic field that tiles - see below)
  //   depth        allocate a DEPTH BUFFER (default false). Feedback scenes
  //                are ordered quads and never need one, but a target that a
  //                real 3D scene is rendered into does - Focus stacks five
  //                planes and relies on the depth test to resolve them.
  //   single       allocate ONE target instead of a pair. Some users only
  //                need the reduced-resolution buffer, not the history:
  //                Nebula raymarches into a half-res target and upscales it
  //                in the same frame, and a second target would be a wasted
  //                allocation. read and write are then the same object and
  //                swap() is a no-op, so the usual call shape still works.
  constructor(options) {
    const opts = options || {};
    this._scale = opts.scale != null ? opts.scale : 0.5;
    this._fixedWidth = opts.width != null ? opts.width : null;
    this._fixedHeight = opts.height != null ? opts.height : null;
    this._type = opts.type != null ? opts.type : THREE.UnsignedByteType;
    this._filter = opts.filter != null ? opts.filter : THREE.LinearFilter;
    this._single = !!opts.single;
    this._depth = !!opts.depth;
    // Clamp by default - a feedback image sampled past its own edge should
    // smear, not wrap around to the far side. RepeatWrapping is for fields
    // that are PERIODIC by construction and meant to tile: Ocean's FFT patch
    // is exactly that, and sampling it clamped turns the whole sea beyond the
    // first patch into one flat colour.
    this._wrap = opts.wrap != null ? opts.wrap : THREE.ClampToEdgeWrapping;
    this._width = 0;
    this._height = 0;
    this._a = null;
    this._b = null;
  }

  get read() { return this._a; }
  get write() { return this._b; }
  get width() { return this._width; }
  get height() { return this._height; }

  _makeTarget(w, h) {
    return new THREE.WebGLRenderTarget(w, h, {
      minFilter: this._filter,
      magFilter: this._filter,
      wrapS: this._wrap,
      wrapT: this._wrap,
      format: THREE.RGBAFormat,
      type: this._type,
      depthBuffer: this._depth,
      stencilBuffer: false,
      generateMipmaps: false
    });
  }

  // Matches the pair to the renderer's CURRENT drawing buffer size. Returns
  // true when the buffers were (re)created - the caller's cue that the loop
  // restarted from black and any fade-in state should reset.
  ensureSize(renderer) {
    let w, h;
    if (this._fixedWidth != null) {
      w = this._fixedWidth;
      h = this._fixedHeight != null ? this._fixedHeight : this._fixedWidth;
    } else {
      const size = renderer.getDrawingBufferSize(_scratchSize);
      w = Math.max(2, Math.round(size.x * this._scale));
      h = Math.max(2, Math.round(size.y * this._scale));
    }
    if (w === this._width && h === this._height && this._a) return false;
    this.dispose();
    this._width = w;
    this._height = h;
    this._a = this._makeTarget(w, h);
    this._b = this._single ? this._a : this._makeTarget(w, h);
    this.clear(renderer);
    return true;
  }

  swap() {
    if (this._single) return;
    const t = this._a;
    this._a = this._b;
    this._b = t;
  }

  // Both targets to opaque black - the loop's zero state.
  clear(renderer) {
    if (!this._a) return;
    const prev = renderer.getRenderTarget();
    const prevColor = renderer.getClearColor(_scratchColor);
    const prevAlpha = renderer.getClearAlpha();
    renderer.setClearColor(0x000000, 1);
    renderer.setRenderTarget(this._a);
    renderer.clear(true, false, false);
    if (this._b !== this._a) {
      renderer.setRenderTarget(this._b);
      renderer.clear(true, false, false);
    }
    renderer.setRenderTarget(prev);
    renderer.setClearColor(prevColor, prevAlpha);
  }

  dispose() {
    if (this._a) this._a.dispose();
    if (this._b && this._b !== this._a) this._b.dispose();
    this._a = null;
    this._b = null;
    this._width = 0;
    this._height = 0;
  }
}

const _scratchSize = new THREE.Vector2();
const _scratchColor = new THREE.Color();
